import {
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSelections,
  PricingLastInvoiceFilterValue,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

export type PricingFilterUrlParams = Record<string, string | undefined>;

const rangeParamKey = (sectionId: string, bound: 'max' | 'min'): string =>
  bound === 'min' ? `${sectionId}Min` : `${sectionId}Max`;

const encodeBound = (
  sectionId: string,
  bound: { max: number | null; min: number | null } | undefined,
  params: PricingFilterUrlParams,
): void => {
  params[rangeParamKey(sectionId, 'min')] =
    bound?.min === null || bound?.min === undefined
      ? undefined
      : String(bound.min);
  params[rangeParamKey(sectionId, 'max')] =
    bound?.max === null || bound?.max === undefined
      ? undefined
      : String(bound.max);
};

export const encodeFiltersToParams = <T>(
  { pills, ranges }: PricingFilterSelections,
  sections: PricingFilterSection<T>[],
): PricingFilterUrlParams => {
  const params: PricingFilterUrlParams = {};

  for (const section of sections) {
    if (section.kind === PricingFilterSectionKind.Range) {
      encodeBound(section.id, ranges[section.id], params);
      continue;
    }

    const values = pills[section.id];
    params[section.id] = values?.length ? values.join(',') : undefined;

    if (section.kind === PricingFilterSectionKind.DateRange) {
      encodeBound(section.id, ranges[section.id], params);
    }
  }

  return params;
};

const decodePills = (
  sectionId: string,
  allowed: Set<string>,
  params: PricingFilterUrlParams,
): string[] | null => {
  const raw = params[sectionId];
  if (!raw) {
    return null;
  }

  const values = raw.split(',').filter((value) => allowed.has(value));

  return values.length ? values : null;
};

const parseBound = (raw: string | undefined): number | null => {
  // `Number('')` is 0, so an empty param has to be rejected before parsing or
  // `?priceMin=` reads as a real zero bound.
  if (raw === undefined || raw.trim() === '') {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const decodeBound = (
  sectionId: string,
  params: PricingFilterUrlParams,
): { max: number | null; min: number | null } | null => {
  const min = parseBound(params[rangeParamKey(sectionId, 'min')]);
  const max = parseBound(params[rangeParamKey(sectionId, 'max')]);

  return min === null && max === null ? null : { max, min };
};

const LAST_INVOICE_VALUES = new Set(
  Object.values(PricingLastInvoiceFilterValue),
);

export const decodeFiltersFromParams = <T>(
  params: PricingFilterUrlParams,
  sections: PricingFilterSection<T>[],
): PricingFilterSelections => {
  const pills: Record<string, string[]> = {};
  const ranges: Record<string, { max: number | null; min: number | null }> = {};

  for (const section of sections) {
    if (section.kind === PricingFilterSectionKind.Range) {
      const bound = decodeBound(section.id, params);
      if (bound) {
        ranges[section.id] = bound;
      }
      continue;
    }

    const allowed =
      section.kind === PricingFilterSectionKind.Pill
        ? new Set(section.options.map((option) => option.value))
        : LAST_INVOICE_VALUES;
    const values = decodePills(section.id, allowed, params);
    if (values) {
      pills[section.id] = values;
    }

    if (section.kind === PricingFilterSectionKind.DateRange) {
      const bound = decodeBound(section.id, params);
      if (bound) {
        ranges[section.id] = bound;
      }
    }
  }

  return { pills, ranges };
};

const canonicalizeParams = (params: PricingFilterUrlParams): string =>
  JSON.stringify(
    Object.entries(params)
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
      .map(([key, value]) => [key, value.split(',').sort().join(',')] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
  );

export const filtersEqual = <T>(
  a: PricingFilterSelections,
  b: PricingFilterSelections,
  sections: PricingFilterSection<T>[],
): boolean =>
  canonicalizeParams(encodeFiltersToParams(a, sections)) ===
  canonicalizeParams(encodeFiltersToParams(b, sections));
